import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSetFavoriteProduct } from "@/api/hooks";
import { useRequireAuth } from "@/features/auth-gate";
import { useToast } from "@/features/toast";
import { useFavoriteProductsStore } from "../store/favorite-products-store";

/**
 * Tek bir ürünün kalbi.
 *
 * Kart da ürün detayı da bunu çağırır: durum store'dan okunur, tıklama
 * iyimser yazılır, sunucu yanıtı gelince onun dediği yazılır, hata olursa
 * eski durum geri alınır. Misafirde istek atılmaz — auth gate açılır.
 */
export const useFavoriteToggle = (productId: string | null | undefined) => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const { requireAuth } = useRequireAuth();
  const { mutateAsync } = useSetFavoriteProduct();

  const isFavorite = useFavoriteProductsStore((state) =>
    productId ? state.known[productId] === true : false,
  );
  const isPending = useFavoriteProductsStore((state) =>
    productId ? state.pending[productId] === true : false,
  );
  const setFavorite = useFavoriteProductsStore((state) => state.setFavorite);
  const setPending = useFavoriteProductsStore((state) => state.setPending);

  const toggle = useCallback(async () => {
    if (!productId || isPending) return;
    // Oturum yoksa gate açılır; kullanıcı giriş yapınca kalbe yeniden basar.
    if (!requireAuth(undefined, { title: t("favorites.authGateTitle") })) return;

    const next = !isFavorite;
    setFavorite(productId, next);
    setPending(productId, true);

    try {
      const response = await mutateAsync({
        productId,
        action: next ? "add" : "remove",
      });
      // Uç iki yönde de idempotent; yanıttaki durum çağrı sonrası gerçek durum.
      setFavorite(productId, response.data.isFavorite);
      success(
        next ? t("productDetail.addedToFavorites") : t("favorites.removed"),
        { title: t("common.favorites") },
      );
    } catch {
      setFavorite(productId, !next);
      error(t("favorites.error"), { title: t("common.favorites") });
    } finally {
      setPending(productId, false);
    }
  }, [
    productId,
    isFavorite,
    isPending,
    requireAuth,
    mutateAsync,
    setFavorite,
    setPending,
    success,
    error,
    t,
  ]);

  return { isFavorite, isPending, toggle };
};
